import { useState, useMemo } from 'react';
import {
  Download,
  LayoutGrid,
  Loader2,
  Printer,
  TrendingUp,
  Package,
  DollarSign,
  Globe,
  MapPin,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { format } from 'date-fns';
import { DateRangeSelect, type DateRangeValue } from '@/components/ui/date-range-select';
import { datePresets, presetValue } from '@/lib/datePresets';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const DATE_RANGE_PRESETS = datePresets('today', 'week', 'month', 'last_month', 'year');

const CHART_COLORS = [
  'hsl(217, 91%, 60%)',  // blue
  'hsl(217, 91%, 65%)',  // lighter blue
  'hsl(217, 91%, 55%)',  // darker blue
];

export default function CategoryReports() {
  const { currentLocation } = useLocationStore();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'Category report' });

  const [selectedLocationId, setSelectedLocationId] = useState<string>(currentLocation?.id || 'all');
  const [dateSel, setDateSel] = useState<DateRangeValue>(() => presetValue('month'));

  // Determine which location to use
  const queryLocationId = selectedLocationId === 'all' ? currentLocation?.id : selectedLocationId;

  // Calculate date filters
  const dateFilters = useMemo(
    () => ({ start: dateSel.start.toISOString(), end: dateSel.end.toISOString() }),
    [dateSel]
  );

  const dateRangeLabel = useMemo(() => {
    const start = new Date(dateFilters.start);
    const end = new Date(dateFilters.end);
    return `${format(start, 'yyyy-MM-dd')} - ${format(end, 'yyyy-MM-dd')}`;
  }, [dateFilters]);

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['categories-list', queryLocationId],
    queryFn: async () => {
      if (!queryLocationId) return [];
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, parent_id')
        .eq('location_id', queryLocationId)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!queryLocationId,
  });

  // Fetch products with their categories
  const { data: products } = useQuery({
    queryKey: ['products-categories', queryLocationId],
    queryFn: async () => {
      if (!queryLocationId) return [];
      const { data, error } = await supabase
        .from('products')
        .select('id, name, category_id, price, cost')
        .eq('location_id', queryLocationId)
        .eq('is_active', true);
      if (error) throw error;
      return data || [];
    },
    enabled: !!queryLocationId,
  });

  // Fetch order items for the period
  const { data: orderItems, isLoading } = useQuery({
    queryKey: ['category-report-items', queryLocationId, dateFilters],
    queryFn: async () => {
      if (!queryLocationId) return [];

      const { data, error } = await supabase
        .from('order_items')
        .select(`
          id,
          product_id,
          quantity,
          total,
          unit_price,
          discount_amount,
          tax_amount,
          order:orders!inner(
            location_id,
            created_at,
            status
          )
        `)
        .eq('order.location_id', queryLocationId)
        .gte('order.created_at', dateFilters.start)
        .lte('order.created_at', dateFilters.end)
        .in('order.status', ['completed', 'ready', 'sent']);

      if (error) throw error;
      return data || [];
    },
    enabled: !!queryLocationId,
  });

  // Process category data
  const categoryData = useMemo(() => {
    if (!orderItems?.length || !categories?.length || !products?.length) return [];

    // Create product to category map
    const productCategoryMap = new Map<string, string>();
    products.forEach(p => {
      if (p.category_id) {
        productCategoryMap.set(p.id, p.category_id);
      }
    });

    // Create category name map
    const categoryNameMap = new Map<string, string>();
    categories.forEach(c => {
      categoryNameMap.set(c.id, c.name);
    });

    // Aggregate by category
    const categoryMap = new Map<string, {
      revenue: number;
      itemsSold: number;
      orderCount: Set<string>;
      cost: number;
    }>();

    orderItems.forEach(item => {
      const categoryId = item.product_id 
        ? productCategoryMap.get(item.product_id) || 'uncategorized'
        : 'uncategorized';

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          revenue: 0,
          itemsSold: 0,
          orderCount: new Set(),
          cost: 0,
        });
      }

      const cat = categoryMap.get(categoryId)!;
      cat.revenue += item.total || 0;
      cat.itemsSold += item.quantity || 0;
      
      // Find product cost
      const product = products.find(p => p.id === item.product_id);
      if (product?.cost) {
        cat.cost += product.cost * (item.quantity || 0);
      }
    });

    const totalRevenue = Array.from(categoryMap.values()).reduce((sum, c) => sum + c.revenue, 0);

    return Array.from(categoryMap.entries())
      .map(([categoryId, data]) => ({
        id: categoryId,
        name: categoryId === 'uncategorized' 
          ? 'Uncategorized' 
          : categoryNameMap.get(categoryId) || 'Unknown',
        revenue: data.revenue,
        itemsSold: data.itemsSold,
        cost: data.cost,
        profit: data.revenue - data.cost,
        margin: data.revenue > 0 ? ((data.revenue - data.cost) / data.revenue) * 100 : 0,
        percentage: totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [orderItems, categories, products]);

  // Summary stats
  const summaryStats = useMemo(() => {
    if (!categoryData.length) return null;

    const totalRevenue = categoryData.reduce((sum, c) => sum + c.revenue, 0);
    const totalItems = categoryData.reduce((sum, c) => sum + c.itemsSold, 0);
    const totalProfit = categoryData.reduce((sum, c) => sum + c.profit, 0);
    const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      categoriesCount: categoryData.length,
      totalRevenue,
      totalItems,
      totalProfit,
      avgMargin,
    };
  }, [categoryData]);

  // Chart data
  const chartData = useMemo(() => {
    return categoryData.slice(0, 15).map(cat => ({
      name: cat.name.length > 20 ? cat.name.slice(0, 20) + '...' : cat.name,
      fullName: cat.name,
      revenue: cat.revenue,
    }));
  }, [categoryData]);

  const chartConfig: ChartConfig = {
    revenue: { label: 'Revenue', color: CHART_COLORS[0] },
  };

  // Export CSV
  const exportToCSV = () => {
    if (!categoryData.length) return;

    const headers = ['Category', 'Revenue', 'Items Sold', 'Cost', 'Profit', 'Margin %', '% of Total'];
    const rows = categoryData.map(cat => [
      cat.name,
      cat.revenue.toFixed(2),
      cat.itemsSold,
      cat.cost.toFixed(2),
      cat.profit.toFixed(2),
      cat.margin.toFixed(1),
      cat.percentage.toFixed(1),
    ]);

    // Add totals row
    rows.push([
      'TOTAL',
      categoryData.reduce((sum, c) => sum + c.revenue, 0).toFixed(2),
      categoryData.reduce((sum, c) => sum + c.itemsSold, 0).toString(),
      categoryData.reduce((sum, c) => sum + c.cost, 0).toFixed(2),
      categoryData.reduce((sum, c) => sum + c.profit, 0).toFixed(2),
      '',
      '100.0',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `category-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Category Reports</h1>
          <p className="text-sm text-muted-foreground">
            View total revenue by product category
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={exportToCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Location Selector */}
        <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <div className="flex items-center gap-2">
                <Globe className="h-3.5 w-3.5" />
                <span>All locations</span>
              </div>
            </SelectItem>
            {locations.length > 0 && <div className="my-1 h-px bg-border" />}
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{loc.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DateRangeSelect presets={DATE_RANGE_PRESETS} value={dateSel} onChange={setDateSel} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Categories</span>
              <LayoutGrid className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summaryStats?.categoriesCount || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Revenue</span>
              <DollarSign className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalRevenue || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Items Sold</span>
              <Package className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : (summaryStats?.totalItems || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Gross Profit</span>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalProfit || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {(summaryStats?.avgMargin || 0).toFixed(1)}% margin
            </p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Category Revenue Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Product Category Revenues</CardTitle>
              <CardDescription>Revenue breakdown by category</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ChartContainer config={chartConfig} className="h-[400px]">
                  <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={true} vertical={false} />
                    <XAxis
                      type="number"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      width={120}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          labelFormatter={(_, payload: any) => {
                            if (payload?.[0]?.payload?.fullName) {
                              return payload[0].payload.fullName;
                            }
                            return '';
                          }}
                          valueFormatter={(value) => formatCurrency(value as number)}
                        />
                      }
                    />
                    <Bar 
                      dataKey="revenue" 
                      fill={CHART_COLORS[0]} 
                      radius={[0, 4, 4, 0]}
                      maxBarSize={30}
                    />
                  </BarChart>
                </ChartContainer>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                  No category data
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category Table */}
          <Card>
            <CardHeader>
              <CardTitle>Category Breakdown</CardTitle>
              <CardDescription>
                Detailed metrics by category
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Items Sold</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead className="text-right w-[150px]">% of Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryData.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell className="font-medium">{cat.name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cat.itemsSold.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(cat.revenue)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatCurrency(cat.cost)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-green-600">
                        {formatCurrency(cat.profit)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cat.margin.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Progress 
                            value={cat.percentage} 
                            className="h-2 w-20"
                          />
                          <span className="w-12 text-right tabular-nums text-muted-foreground">
                            {cat.percentage.toFixed(1)}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {categoryData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No category data found for this period
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Totals Row */}
                  {categoryData.length > 0 && (
                    <TableRow className="bg-muted/30 font-semibold">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {summaryStats?.totalItems?.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(summaryStats?.totalRevenue || 0)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(categoryData.reduce((sum, c) => sum + c.cost, 0))}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-green-600">
                        {formatCurrency(summaryStats?.totalProfit || 0)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(summaryStats?.avgMargin || 0).toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        100%
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Print template */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>Category Report</h1>
            <div className="print-meta">
              {currentLocation?.name || 'All Locations'} • {dateRangeLabel}
            </div>
          </div>
          <div className="print-meta">
            Generated on {new Date().toLocaleString()}
          </div>
        </div>

        <div className="print-summary">
          <div className="print-card">
            <div className="label">Categories</div>
            <div className="value">{summaryStats?.categoriesCount || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Revenue</div>
            <div className="value">{formatCurrency(summaryStats?.totalRevenue || 0)}</div>
          </div>
          <div className="print-card">
            <div className="label">Items Sold</div>
            <div className="value">{summaryStats?.totalItems || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Gross Profit</div>
            <div className="value">{formatCurrency(summaryStats?.totalProfit || 0)}</div>
          </div>
        </div>

        <div className="print-section">
          <h2>Category Breakdown</h2>
          {categoryData.length > 0 ? (
            <table className="print-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Items Sold</th>
                  <th>Revenue</th>
                  <th>Cost</th>
                  <th>Profit</th>
                  <th>Margin</th>
                  <th>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {categoryData.map((cat) => (
                  <tr key={cat.id}>
                    <td>{cat.name}</td>
                    <td>{cat.itemsSold}</td>
                    <td>{formatCurrency(cat.revenue)}</td>
                    <td>{formatCurrency(cat.cost)}</td>
                    <td>{formatCurrency(cat.profit)}</td>
                    <td>{cat.margin.toFixed(1)}%</td>
                    <td>{cat.percentage.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td>{summaryStats?.totalItems}</td>
                  <td>{formatCurrency(summaryStats?.totalRevenue || 0)}</td>
                  <td>{formatCurrency(categoryData.reduce((sum, c) => sum + c.cost, 0))}</td>
                  <td>{formatCurrency(summaryStats?.totalProfit || 0)}</td>
                  <td>{(summaryStats?.avgMargin || 0).toFixed(1)}%</td>
                  <td>100%</td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="print-meta">No category data found.</div>
          )}
        </div>
      </div>
    </div>
  );
}

